import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Pencil, Sparkles, Wand2, Loader2, X, Save, Undo2, Check } from 'lucide-react';

const friendly = (e, fb) => {
  const d = e?.response?.data?.detail || e?.message || fb;
  return String(d).replace(/^MISSING_KEY:[^|]*\|/, '');
};

/**
 * Reusable frame edit popover: "Edit prompt" (regenerate from an editable prompt)
 * + "Refine with comment" (image-to-image tweak of the existing frame).
 * which: 'first' | 'last'
 */
export function FrameEditor({ projectId, sceneId, which, scene, imageProvider = 'gemini', onUpdated, refreshJobs, align = 'right', renderTrigger }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('prompt'); // 'prompt' | 'refine'
  const promptField = `${which}_frame_prompt`;
  const framePath = which === 'first' ? scene?.first_frame_path : scene?.last_frame_path;
  const [promptText, setPromptText] = useState(scene?.[promptField] || '');
  const [comment, setComment] = useState('');
  const [strength, setStrength] = useState(70);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [compare, setCompare] = useState(null); // { before, after }
  const pollRef = useRef(null);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => { setPromptText(scene?.[promptField] || ''); }, [scene, promptField]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Popover is portaled to <body> so card overflow/hover-opacity can't clip or hide it
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const W = 300, H = 330, vw = window.innerWidth, vh = window.innerHeight;
      let left = align === 'right' ? r.right - W : r.left;
      left = Math.max(8, Math.min(left, vw - W - 8));
      const below = vh - r.bottom;
      const top = below >= H || r.top < H ? r.bottom + 8 : r.top - H - 8;
      setPos({ top: Math.max(8, top), left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, align]);

  const label = which === 'first' ? 'Opening Frame' : 'Closing Frame';

  const poll = () => new Promise((resolve, reject) => {
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 3;
      try {
        const r = await api.get(`/projects/${projectId}/scenes/${sceneId}/frame-gen-status`, { params: { _t: Date.now() } });
        const st = r.data?.status;
        if (r.data?.progress) setProgress(r.data.progress);
        if (st === 'complete') {
          clearInterval(pollRef.current);
          resolve(r.data?.scene || null);
        } else if (st === 'failed') {
          clearInterval(pollRef.current);
          reject(new Error(r.data?.error || 'Generation failed'));
        } else if (elapsed >= 180) {
          clearInterval(pollRef.current);
          reject(new Error('Timed out — check the Activity panel.'));
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  });

  const savePrompt = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/projects/${projectId}/scenes/${sceneId}`, { [promptField]: promptText });
      onUpdated?.(res.data);
      toast.success('Prompt saved');
    } catch { toast.error('Failed to save prompt'); }
    finally { setBusy(false); }
  };

  const saveAndRegen = async () => {
    setBusy(true); setProgress('Saving prompt...');
    try {
      await api.put(`/projects/${projectId}/scenes/${sceneId}`, { [promptField]: promptText });
      setProgress('Regenerating frame...');
      await api.post(`/projects/${projectId}/scenes/${sceneId}/generate-frames?frame=${which}&image_provider=${imageProvider}`, {}, { timeout: 15000 });
      refreshJobs?.();
      const updated = await poll();
      if (updated) onUpdated?.(updated);
      refreshJobs?.();
      toast.success(`${label} regenerated`);
      setOpen(false);
    } catch (e) { toast.error(friendly(e, 'Failed to regenerate')); }
    finally { setBusy(false); setProgress(''); }
  };

  const applyRefine = async () => {
    const c = comment.trim();
    if (!c) { toast.error('Describe the tweak first'); return; }
    const beforePath = framePath;
    setBusy(true); setProgress('Applying your edit...');
    try {
      await api.post(`/projects/${projectId}/scenes/${sceneId}/refine-frame?frame=${which}`, { comment: c, image_provider: imageProvider, strength });
      refreshJobs?.();
      const updated = await poll();
      if (updated) onUpdated?.(updated);
      refreshJobs?.();
      const afterPath = updated?.[which === 'first' ? 'first_frame_path' : 'last_frame_path'];
      toast.success('Edit applied');
      setComment('');
      setOpen(false);
      if (beforePath && afterPath && beforePath !== afterPath) setCompare({ before: beforePath, after: afterPath });
    } catch (e) { toast.error(friendly(e, 'Failed to apply edit')); }
    finally { setBusy(false); setProgress(''); }
  };

  const revertRefine = async () => {
    if (!compare) return;
    setBusy(true);
    try {
      const slot = which === 'first' ? 'first_frame' : 'last_frame';
      const res = await api.post(`/projects/${projectId}/scenes/${sceneId}/use-media`, { slot, path: compare.before });
      onUpdated?.(res.data);
      refreshJobs?.();
      toast.success('Reverted to the original');
      setCompare(null);
    } catch (e) { toast.error(friendly(e, 'Failed to revert')); }
    finally { setBusy(false); }
  };

  return (
    <>
    <div className="relative inline-block" ref={triggerRef}>
      {renderTrigger ? renderTrigger({ open, toggle: () => setOpen(o => !o) }) : (
        <button data-testid={`frame-edit-btn-${which}`} onClick={() => setOpen(o => !o)}
          className="text-xs text-[#FF4500] hover:text-[#FF6A33] flex items-center gap-1 transition-colors">
          <Pencil className="w-3 h-3" /> Edit
        </button>
      )}
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[75]" onClick={() => !busy && setOpen(false)} />
          <div
            data-testid={`frame-edit-panel-${which}`}
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="z-[76] w-[300px] bg-[#15151b] border border-white/10 rounded-xl shadow-2xl p-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white">Edit {label}</span>
              <button onClick={() => !busy && setOpen(false)} className="p-1 rounded hover:bg-white/5 text-[#8A8A93]"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex gap-1 mb-3 p-0.5 bg-white/[0.04] rounded-lg">
              <button data-testid={`frame-edit-tab-prompt-${which}`} onClick={() => setMode('prompt')}
                className={`flex-1 text-[11px] py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1 ${mode === 'prompt' ? 'bg-[#FF4500]/20 text-[#FF4500]' : 'text-[#8A8A93] hover:text-white'}`}>
                <Sparkles className="w-3 h-3" /> Edit prompt
              </button>
              <button data-testid={`frame-edit-tab-refine-${which}`} onClick={() => setMode('refine')}
                className={`flex-1 text-[11px] py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-1 ${mode === 'refine' ? 'bg-[#A78BFA]/20 text-[#A78BFA]' : 'text-[#8A8A93] hover:text-white'}`}>
                <Wand2 className="w-3 h-3" /> Refine
              </button>
            </div>

            {mode === 'prompt' ? (
              <div>
                <textarea
                  data-testid={`frame-edit-prompt-input-${which}`}
                  value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  disabled={busy}
                  className="w-full h-28 resize-none rounded-lg bg-[#0a0a0e] border border-white/10 text-xs text-white p-2.5 outline-none focus:border-[#FF4500]/40 leading-relaxed"
                  placeholder="Custom prompt for this frame. Leave blank to auto-generate from the scene's actions."
                />
                <div className="flex gap-2 mt-2">
                  <button data-testid={`frame-edit-save-prompt-${which}`} onClick={savePrompt} disabled={busy}
                    className="flex-1 text-[11px] py-2 rounded-lg bg-white/[0.06] text-[#8A8A93] hover:text-white disabled:opacity-40 flex items-center justify-center gap-1">
                    <Save className="w-3 h-3" /> Save
                  </button>
                  <button data-testid={`frame-edit-regen-${which}`} onClick={saveAndRegen} disabled={busy}
                    className="flex-1 text-[11px] py-2 rounded-lg bg-[#FF4500]/15 text-[#FF4500] hover:bg-[#FF4500]/25 disabled:opacity-40 flex items-center justify-center gap-1">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Save & Regenerate
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {!framePath && (
                  <p className="text-[10px] text-[#FF6A33] mb-2">Generate a frame first, then refine it with a comment.</p>
                )}
                <textarea
                  data-testid={`frame-edit-refine-input-${which}`}
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  disabled={busy || !framePath}
                  className="w-full h-24 resize-none rounded-lg bg-[#0a0a0e] border border-white/10 text-xs text-white p-2.5 outline-none focus:border-[#A78BFA]/40 leading-relaxed disabled:opacity-50"
                  placeholder='A small tweak to the current image, e.g. "make it night time", "remove the hat", "add rain".'
                />
                <div className="mt-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8A93]">Reference strength</label>
                    <span data-testid={`frame-refine-strength-value-${which}`} className="text-[10px] font-semibold text-[#A78BFA]">
                      {strength >= 75 ? 'Subtle' : strength >= 40 ? 'Balanced' : 'Bold'} · {strength}%
                    </span>
                  </div>
                  <input
                    type="range" min="0" max="100" step="5"
                    data-testid={`frame-refine-strength-${which}`}
                    value={strength}
                    disabled={busy || !framePath}
                    onChange={e => setStrength(Number(e.target.value))}
                    className="w-full accent-[#A78BFA] cursor-pointer disabled:opacity-40"
                  />
                  <div className="flex justify-between text-[9px] text-[#55555C] mt-0.5">
                    <span>Freer edit</span>
                    <span>Sticks to original</span>
                  </div>
                </div>
                <button data-testid={`frame-edit-apply-refine-${which}`} onClick={applyRefine} disabled={busy || !framePath}
                  className="w-full mt-2 text-[11px] py-2 rounded-lg bg-[#A78BFA]/15 text-[#A78BFA] hover:bg-[#A78BFA]/25 disabled:opacity-40 flex items-center justify-center gap-1">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Apply edit
                </button>
              </div>
            )}
            {busy && progress && <p className="text-[10px] text-[#8A8A93] mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {progress}</p>}
          </div>
        </>,
        document.body
      )}
    </div>
    {compare && (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" data-testid={`refine-compare-${which}`} onClick={() => !busy && setCompare(null)}>
        <div className="bg-[#15151b] border border-white/10 rounded-2xl p-5 max-w-3xl w-full shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">Before / After — {label}</h3>
            <button onClick={() => !busy && setCompare(null)} className="p-1 rounded hover:bg-white/5 text-[#8A8A93]"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="rounded-lg overflow-hidden border border-white/10 bg-[#0a0a0e] flex items-center justify-center"><img src={getImageUrl(compare.before)} alt="Before" className="w-full object-contain max-h-[52vh]" /></div>
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mt-2 text-center">Before</p>
            </div>
            <div>
              <div className="rounded-lg overflow-hidden border border-[#A78BFA]/40 bg-[#0a0a0e] flex items-center justify-center"><img src={getImageUrl(compare.after)} alt="After" className="w-full object-contain max-h-[52vh]" /></div>
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#A78BFA] mt-2 text-center">After · edited</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button data-testid={`refine-revert-${which}`} onClick={revertRefine} disabled={busy}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-white/[0.06] text-[#8A8A93] hover:text-white disabled:opacity-40 flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Revert to original
            </button>
            <button data-testid={`refine-keep-${which}`} onClick={() => setCompare(null)} disabled={busy}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-[#A78BFA]/20 text-[#A78BFA] hover:bg-[#A78BFA]/30 border border-[#A78BFA]/40 disabled:opacity-40 flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Keep edit
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
